import type { RowSelectionState } from "@tanstack/react-table";
import type { MutableRefObject } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { DispatchTableRow } from "./dispatchTableRow";
import { highlightAnchorIndex, nextHighlightIndex } from "./useDispatchHighlight";

/**
 * The ticked rows: the spreadsheet-style selection for acting on many at once.
 *
 * Ticking any row leaves the one-driver-at-a-time mode (`tickedMode`): the detail folds shut, H and C and R go quiet,
 * A and Shift+M acknowledge the ticked rows, and Escape clears them. Ticked rows are the page's, not the view's: they
 * clear when the view changes and when a bulk action lands. A Shift run anchors on the highlighted row; with none, on
 * the open panel's row, else the row under the pointer, and only then comes in at the end of the list; the anchor is
 * ticked along with the row stepped onto, which also takes the highlight. `shiftSelectRef` is the row last ticked on
 * its own, which a Shift-click on a checkbox extends from.
 */
export function useTickedRows({
  tableRows,
  viewId,
  highlightedId,
  highlight,
  openRowId,
  hoveredRowIdRef,
}: {
  tableRows: readonly DispatchTableRow[];
  /** Ticks reset when it changes. */
  viewId: string;
  highlightedId: string | null;
  highlight: (id: string | null) => void;
  openRowId: string | null;
  /** The row under the pointer, kept in a ref so hovering never re-renders the table. */
  hoveredRowIdRef: MutableRefObject<string | null>;
}): {
  ticks: RowSelectionState;
  setTicks: (updater: RowSelectionState | ((previous: RowSelectionState) => RowSelectionState)) => void;
  tickedRows: DispatchTableRow[];
  tickedMode: boolean;
  toggle: () => void;
  extend: (delta: 1 | -1) => void;
  all: () => void;
  clear: () => void;
  shiftSelectRef: MutableRefObject<string | null>;
} {
  const [ticks, setTicks] = useState<RowSelectionState>({});
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on the view's identity
  useEffect(() => {
    setTicks({});
  }, [viewId]);
  const tickedRows = useMemo(() => tableRows.filter((row) => ticks[row.id]), [tableRows, ticks]);
  const shiftSelectRef = useRef<string | null>(null);

  const toggle = useCallback(() => {
    if (!highlightedId) return;
    shiftSelectRef.current = highlightedId;
    setTicks((previous) => {
      const next = { ...previous };
      if (next[highlightedId]) delete next[highlightedId];
      else next[highlightedId] = true;
      return next;
    });
  }, [highlightedId]);

  const extend = useCallback(
    (delta: 1 | -1) => {
      const anchorIndex = highlightAnchorIndex(tableRows, {
        highlightedId,
        openRowId,
        hoveredRowId: hoveredRowIdRef.current,
      });
      const nextIndex = nextHighlightIndex(anchorIndex, delta, tableRows.length);
      if (nextIndex == null) return;
      const anchorId = anchorIndex === -1 ? null : tableRows[anchorIndex]?.id;
      const nextId = tableRows[nextIndex]?.id;
      if (!nextId) return;
      setTicks((previous) => {
        // At the list's edge the step lands on the anchor itself, so nothing changes and nothing re-renders.
        if (previous[nextId] && (!anchorId || previous[anchorId])) return previous;
        return { ...previous, ...(anchorId ? { [anchorId]: true } : {}), [nextId]: true };
      });
      highlight(nextId);
    },
    [tableRows, highlightedId, openRowId, hoveredRowIdRef, highlight],
  );

  const all = useCallback(
    () =>
      setTicks((previous) =>
        tableRows.length === Object.keys(previous).length && tableRows.every((row) => previous[row.id])
          ? previous
          : Object.fromEntries(tableRows.map((row) => [row.id, true])),
      ),
    [tableRows],
  );
  const clear = useCallback(() => setTicks({}), []);

  return { ticks, setTicks, tickedRows, tickedMode: tickedRows.length > 0, toggle, extend, all, clear, shiftSelectRef };
}
